import React, { useState, useEffect, useRef } from 'react';
import { 
  User, 
  Globe, 
  Target, 
  Cpu, 
  CreditCard, 
  Check, 
  Copy, 
  RefreshCw, 
  Trash2, 
  ExternalLink, 
  AlertTriangle, 
  ShieldCheck, 
  ShieldAlert, 
  ArrowLeft,
  ChevronRight,
  Info,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  Link as LinkIcon,
  Eye,
  EyeOff
} from 'lucide-react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

export const NETWORK_TRACKING_CONFIGS = {
  MaxBounty: {
    id: 'MaxBounty',
    name: 'MaxBounty',
    label: 'MaxBounty',
    subIdParam: 's2',
    subIdOptions: ['s1', 's2', 's3', 's4', 's5'],
    secondarySubIdParam: 's1',
    postbackMacros: {
      clickId: '#S2#',
      s1: '#S1#',
      s2: '#S2#',
      s3: '#S3#',
      s4: '#S4#',
      s5: '#S5#',
      payout: '#RATE#',
      txId: '#TRANSID#',
      offerId: '#OFFID#'
    },
    postbackQueryTemplate: 'network=MaxBounty&click_id=#S2#&s1=#S1#&s2=#S2#&s3=#S3#&s4=#S4#&s5=#S5#&payout=#RATE#&tx_id=#TRANSID#&offer_id=#OFFID#',
    notes: 'Confirmed via MaxBounty Callback Spec: pick which sub-ID (s1–s5, default s2) AffiliateFlow appends on /r/ offer links so it never collides with your existing tracker. Uses #S1#–#S5#, #RATE# (payout), #TRANSID# (transaction ID), #OFFID# (offer ID).'
  },
  CPALead: {
    id: 'CPALead',
    name: 'CPALead',
    label: 'CPALead',
    subIdParam: 'subid',
    subIdOptions: ['subid', 'subid2', 'subid3'],
    secondarySubIdParam: 'subid2',
    postbackMacros: {
      clickId: '{subid}',
      payout: '{payout}',
      txId: '{lead_id}',
      offerId: '{campaign_id}',
      status: '{status}'
    },
    postbackQueryTemplate: 'network=CPALead&click_id={subid}&payout={payout}&tx_id={lead_id}&offer_id={campaign_id}&status={status}',
    notes: 'Confirmed via official CPAlead Postback Docs (cpalead.com/documentation/postback): passes ?subid={clickId} on offer redirects and receives {subid}, {payout}, {lead_id}, {campaign_id}, {status}.'
  },
  ClickBank: {
    id: 'ClickBank',
    name: 'ClickBank',
    label: 'ClickBank',
    subIdParam: 'extclid',
    subIdOptions: ['extclid', 'tid'],
    secondarySubIdParam: 'tid',
    postbackMacros: {
      clickId: '{extclid}',
      tid: '{tid}',
      payout: '{affiliate_earnings}',
      txId: '{order_id}',
      offerId: '{item}',
      status: '{transaction_type}'
    },
    postbackQueryTemplate: 'network=ClickBank&click_id={extclid}&tid={tid}&extclid={extclid}&payout={affiliate_earnings}&tx_id={order_id}&offer_id={item}&status={transaction_type}',
    notes: 'Confirmed via ClickBank Support (support.clickbank.com — Postback/Pixels Integration Guide & INS v8.0): appends ?extclid={clickId}&tid={clickId} on HopLinks. Paste the S2S URL under Integrations → Postback/Pixels, or use the INS v8.0 encrypted POST webhook URL below.'
  },
  Custom: {
    id: 'Custom',
    name: 'Custom Network',
    label: 'Custom Network',
    subIdParam: 'subid',
    subIdOptions: ['subid', 'click_id', 's1', 's2', 'aff_sub'],
    secondarySubIdParam: 'click_id',
    postbackMacros: {
      clickId: '{clickid}',
      payout: '{payout}',
      txId: '{txid}',
      offerId: '{offer_id}',
      status: '{status}'
    },
    postbackQueryTemplate: 'network=Custom&click_id={clickid}&payout={payout}&tx_id={txid}&offer_id={offer_id}&status={status}',
    notes: 'Generic template for custom affiliate networks — replace {clickid}, {payout}, {txid}, {offer_id}, {status} with your network\'s macros before pasting.'
  }
};

export function buildNetworkPostbackUrlFromConfig(netKey, token, postbackHost = 'affiliateflow.ai', chosenSubId = '') {
  const cfg = NETWORK_TRACKING_CONFIGS[netKey] || NETWORK_TRACKING_CONFIGS.Custom;
  if (!token) return '';
  const host = (postbackHost || 'affiliateflow.ai').replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  const sub = String(chosenSubId || cfg.subIdParam).trim();
  let queryTemplate = cfg.postbackQueryTemplate;
  if (netKey === 'MaxBounty') {
    const macro = `#${sub.toUpperCase()}#`;
    queryTemplate = `network=MaxBounty&click_id=${macro}&s1=#S1#&s2=#S2#&s3=#S3#&s4=#S4#&s5=#S5#&payout=#RATE#&tx_id=#TRANSID#&offer_id=#OFFID#`;
  } else if (netKey === 'CPALead') {
    const macro = `{${sub}}`;
    queryTemplate = `network=CPALead&click_id=${macro}&payout={payout}&tx_id={lead_id}&offer_id={campaign_id}&status={status}`;
  } else if (netKey === 'ClickBank') {
    const macro = `{${sub}}`;
    queryTemplate = `network=ClickBank&click_id=${macro}&tid={tid}&extclid={extclid}&payout={affiliate_earnings}&tx_id={order_id}&offer_id={item}&status={transaction_type}`;
  }
  return `https://${host}/api/postback?${queryTemplate}&token=${token}`;
}

export default function Settings({ onBack, onUsernameUpdated }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: string }

  // ── Profile State ──────────────────────────────────────────────────────────
  const [profile, setProfile] = useState({
    displayName: '',
    username: '',
    email: '',
    timezone: 'America/Los_Angeles',
    defaultCurrency: 'USD'
  });
  const [initialUsername, setInitialUsername] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  // ── Domains State ──────────────────────────────────────────────────────────
  const [domains, setDomains] = useState([]);
  const [newHostname, setNewHostname] = useState('');
  const [connectingDomain, setConnectingDomain] = useState(false);
  const [checkingDomainId, setCheckingDomainId] = useState(null);
  const [userCampaignSlugs, setUserCampaignSlugs] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);

  // ── Tracking State ─────────────────────────────────────────────────────────
  const [tracking, setTracking] = useState({
    postbackUrl: '',
    postbackToken: '',
    networkSubIds: {
      MaxBounty: 's2',
      CPALead: 'subid',
      ClickBank: 'extclid',
      Custom: 'subid'
    },
    networkPostbacks: null,
    forwardingRules: [],
    forwardLogs: [],
    defaultTrafficSource: 'MGID',
    defaultCostModel: 'CPC',
    offerHealthCheck: true
  });
  const [trackingSaving, setTrackingSaving] = useState(false);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  const [showPostbackToken, setShowPostbackToken] = useState(false);
  const [newForwardRule, setNewForwardRule] = useState({
    name: '',
    network: 'MaxBounty',
    destinationUrlTemplate: '',
    condition: 'unmatched'
  });
  const [savingForwardRule, setSavingForwardRule] = useState(false);
  const [testingRuleId, setTestingRuleId] = useState(null);
  const [forwardTestStatus, setForwardTestStatus] = useState({}); // key -> { ok, statusCode, durationMs, error }
  const [loadingForwardLogs, setLoadingForwardLogs] = useState(false);

  // ── Integrations State ─────────────────────────────────────────────────────
  const [integrations, setIntegrations] = useState({
    ga4MeasurementId: '',
    hasClickBankKey: false,
    clickBankKeyMasked: '',
    hasMetaToken: false,
    metaTokenMasked: ''
  });
  const [rawClickBankKey, setRawClickBankKey] = useState('');
  const [rawMetaToken, setRawMetaToken] = useState('');
  const [integrationsSaving, setIntegrationsSaving] = useState(false);
  const [testingIntegration, setTestingIntegration] = useState(null); // 'clickbank' | 'meta'
  const [testResult, setTestResult] = useState(null); // { type, success, message }

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const copyToClipboard = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const getDnsHost = (hostname) => {
    if (!hostname) return '@';
    const parts = hostname.toLowerCase().split('.');
    if (parts.length <= 2) return '@';
    return parts.slice(0, -2).join('.');
  };

  const waitForAuthUser = () => {
    if (auth.currentUser) return Promise.resolve(auth.currentUser);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        unsub();
        resolve(auth.currentUser || null);
      }, 4000);
      const unsub = onAuthStateChanged(auth, (u) => {
        clearTimeout(timer);
        unsub();
        resolve(u || null);
      });
    });
  };

  const getAuthHeader = async () => {
    const user = auth.currentUser || await waitForAuthUser();
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // ── Initial Data Loaders (runs when Firebase Auth user is ready) ──────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadProfile();
        loadDomains();
        loadTracking();
        loadIntegrations();
        loadCampaignSlugs();
      }
    });
    return () => unsubscribe();
  }, []);

  // Refresh domains whenever user opens the Domains tab
  useEffect(() => {
    if (activeTab === 'domains') {
      loadDomains(domains.length === 0);
    } else if (activeTab === 'tracking' && !tracking.postbackToken) {
      loadTracking();
    } else if (activeTab === 'profile' && !profile.email) {
      loadProfile();
    } else if (activeTab === 'integrations') {
      loadIntegrations();
    }
  }, [activeTab]);

  // ── Auto-Poll Pending Domains every 12 seconds ──────────────────────────────
  const isPollingRef = useRef(false);

  useEffect(() => {
    if (activeTab !== 'domains') return;
    const hasPending = domains.some(d => d.status !== 'active');
    if (!hasPending) return;

    const interval = setInterval(async () => {
      if (isPollingRef.current) return;
      isPollingRef.current = true;
      try {
        const headers = await getAuthHeader();
        const pendingList = domains.filter(d => d.status !== 'active');
        for (const pd of pendingList) {
          try {
            const res = await fetch(`/api/settings/domains/${pd.id}/status`, { headers });
            if (res.ok) {
              const data = await res.json();
              if (data.domain) {
                setDomains(prev => prev.map(d => d.id === pd.id ? data.domain : d));
                if (data.domain.status === 'active') {
                  showNotification(`🎉 ${data.domain.hostname} is now Active and SSL verified!`);
                }
              }
            }
          } catch (e) {
            // Ignore background polling glitches
          }
        }
      } catch (err) {
        // Ignore session glitches during polling
      } finally {
        isPollingRef.current = false;
      }
    }, 12000);

    return () => clearInterval(interval);
  }, [activeTab, domains]);

  const loadProfile = async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/profile', { headers });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setInitialUsername(data.username);
      }
    } catch (err) {
      console.warn('Failed to load profile:', err);
    }
  };

  const loadDomains = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/domains', { headers });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data.domains) ? [...data.domains] : [];
        list.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return String(a.hostname || '').localeCompare(String(b.hostname || ''));
        });
        setDomains(list);
      }
    } catch (err) {
      console.warn('Failed to load domains:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadCampaignSlugs = async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/campaign-slugs', { headers });
      if (res.ok) {
        const data = await res.json();
        setUserCampaignSlugs(data.slugs || []);
      }
    } catch (err) {
      console.warn('Failed to load campaign slugs:', err);
    }
  };

  const loadTracking = async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/tracking', { headers });
      if (res.ok) {
        const data = await res.json();
        setTracking(data);
      }
    } catch (err) {
      console.warn('Failed to load tracking settings:', err);
    }
  };

  const loadIntegrations = async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/integrations', { headers });
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data);
      }
    } catch (err) {
      console.warn('Failed to load integrations:', err);
    }
  };

  // ── Profile Handlers ───────────────────────────────────────────────────────
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/profile', {
        method: 'PUT',
        headers,
        body: JSON.stringify(profile)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save profile');
      }
      setProfile(data.profile);
      setInitialUsername(data.profile.username);
      showNotification('Profile updated successfully!');
      if (onUsernameUpdated) {
        onUsernameUpdated(data.profile.username);
      }
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Domain Handlers ────────────────────────────────────────────────────────
  const handleConnectDomain = async (e) => {
    e.preventDefault();
    if (!newHostname.trim()) return;
    setConnectingDomain(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/domains', {
        method: 'POST',
        headers,
        body: JSON.stringify({ hostname: newHostname.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to connect domain');
      }
      setNewHostname('');
      showNotification(`Connected ${data.domain.hostname}! Follow DNS instructions below.`);
      if (data.domain) {
        setDomains(prev => [data.domain, ...prev.filter(d => d.id !== data.domain.id)]);
      }
      loadDomains(false);
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setConnectingDomain(false);
    }
  };

  const handleCheckStatus = async (domainId) => {
    setCheckingDomainId(domainId);
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/settings/domains/${domainId}/status`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to check status');
      
      setDomains(prev => prev.map(d => d.id === domainId ? data.domain : d));
      if (data.domain.status === 'active' && data.domain.healthCheck?.ok) {
        showNotification(`${data.domain.hostname} is Active and /health end-to-end check passed!`);
      } else if (data.domain.status === 'active' && data.domain.healthCheck && !data.domain.healthCheck.ok) {
        showNotification(`Cloudflare SSL active, but /health check failed: ${data.domain.healthCheck.error || 'Routing not active'}`, 'error');
      } else {
        showNotification(`Status updated: ${data.domain.status.replace('_', ' ').toUpperCase()}`);
      }
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setCheckingDomainId(null);
    }
  };

  const handleSetDefaultDomain = async (domainId) => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/settings/domains/${domainId}/default`, {
        method: 'PUT',
        headers
      });
      if (!res.ok) throw new Error('Failed to set default domain');
      setDomains(prev => prev.map(d => ({ ...d, isDefault: d.id === domainId })));
      showNotification('Default domain updated');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleSaveRootBehavior = async (domainId, rootBehavior, rootRedirectSlug) => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/settings/domains/${domainId}/root-behavior`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ rootBehavior, rootRedirectSlug })
      });
      if (!res.ok) throw new Error('Failed to update root behavior');
      setDomains(prev => prev.map(d => d.id === domainId ? { ...d, rootBehavior, rootRedirectSlug } : d));
      showNotification('Root domain behavior saved');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleDeleteDomain = async (domainId, hostname) => {
    if (!window.confirm(`Are you sure you want to delete ${hostname}? This will remove it from Cloudflare and stop traffic to it.`)) {
      return;
    }
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/settings/domains/${domainId}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete domain');
      setDomains(prev => prev.filter(d => d.id !== domainId));
      showNotification(`Deleted ${hostname}`);
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  // ── Tracking Handlers ──────────────────────────────────────────────────────
  const handleSubIdChange = async (netKey, newSubId) => {
    const cleanSub = String(newSubId || '').trim();
    if (!cleanSub) return;
    const updatedSubIds = {
      ...(tracking.networkSubIds || { MaxBounty: 's2', CPALead: 'subid', ClickBank: 'extclid', Custom: 'subid' }),
      [netKey]: cleanSub
    };
    setTracking(prev => ({
      ...prev,
      networkSubIds: updatedSubIds
    }));
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/tracking', {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ...tracking,
          networkSubIds: updatedSubIds
        })
      });
      if (res.ok) {
        const data = await res.json();
        setTracking(prev => ({
          ...prev,
          networkSubIds: data.networkSubIds || updatedSubIds,
          networkPostbacks: data.networkPostbacks || prev.networkPostbacks
        }));
        showNotification(`${netKey} AffiliateFlow sub-ID updated to "${cleanSub}"`);
      }
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleSaveForwardRule = async (e) => {
    e.preventDefault();
    if (!newForwardRule.destinationUrlTemplate.trim()) return;
    setSavingForwardRule(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/postback-forwarding/rules', {
        method: 'POST',
        headers,
        body: JSON.stringify(newForwardRule)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save forwarding rule');
      setTracking(prev => ({
        ...prev,
        forwardingRules: [...(prev.forwardingRules || []).filter(r => r.id !== data.rule.id), data.rule]
      }));
      setNewForwardRule(prev => ({ ...prev, name: '', destinationUrlTemplate: '' }));
      showNotification('Postback forwarding rule saved!');
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setSavingForwardRule(false);
    }
  };

  const handleDeleteForwardRule = async (ruleId) => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/settings/postback-forwarding/rules/${ruleId}`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete rule');
      setTracking(prev => ({
        ...prev,
        forwardingRules: (prev.forwardingRules || []).filter(r => r.id !== ruleId)
      }));
      showNotification('Forwarding rule deleted');
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleTestForwardRule = async (ruleOrDraft, statusKey) => {
    setTestingRuleId(statusKey);
    try {
      const headers = await getAuthHeader();
      const payload = ruleOrDraft.id && !ruleOrDraft.isDraft
        ? { ruleId: ruleOrDraft.id }
        : {
            name: ruleOrDraft.name || `${ruleOrDraft.network} Test`,
            network: ruleOrDraft.network,
            destinationUrlTemplate: ruleOrDraft.destinationUrlTemplate,
            condition: ruleOrDraft.condition
          };
      const res = await fetch('/api/settings/postback-forwarding/test', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      setForwardTestStatus(prev => ({
        ...prev,
        [statusKey]: {
          ok: !!data.ok,
          statusCode: data.statusCode || 0,
          durationMs: data.durationMs || 0,
          error: data.error || null
        }
      }));
      if (data.log) {
        setTracking(prev => ({
          ...prev,
          forwardLogs: [data.log, ...(prev.forwardLogs || [])].slice(0, 100)
        }));
      }
      if (!res.ok || !data.ok) {
        showNotification(`Test forward failed: ${data.error || `HTTP ${data.statusCode}`}`, 'error');
      } else {
        showNotification(`Test forward succeeded (HTTP ${data.statusCode} in ${data.durationMs}ms)`);
      }
    } catch (err) {
      setForwardTestStatus(prev => ({
        ...prev,
        [statusKey]: { ok: false, statusCode: 0, durationMs: 0, error: err.message }
      }));
      showNotification(err.message, 'error');
    } finally {
      setTestingRuleId(null);
    }
  };

  const handleRefreshForwardLogs = async () => {
    setLoadingForwardLogs(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/postback-forwarding/logs', { headers });
      if (res.ok) {
        const data = await res.json();
        setTracking(prev => ({ ...prev, forwardLogs: data.logs || [] }));
      }
    } catch (_) {} finally {
      setLoadingForwardLogs(false);
    }
  };

  const handleSaveTracking = async (e) => {
    e.preventDefault();
    setTrackingSaving(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/tracking', {
        method: 'PUT',
        headers,
        body: JSON.stringify(tracking)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save tracking settings');
      setTracking(prev => ({
        ...prev,
        networkSubIds: data.networkSubIds || prev.networkSubIds,
        networkPostbacks: data.networkPostbacks || prev.networkPostbacks
      }));
      showNotification('Tracking defaults saved successfully');
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setTrackingSaving(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('Regenerating your token will invalidate any postback URLs configured in ad networks. Continue?')) {
      return;
    }
    setRegeneratingToken(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/tracking/regenerate-token', {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error('Failed to regenerate token');
      setTracking(prev => ({
        ...prev,
        postbackToken: data.postbackToken,
        postbackUrl: data.postbackUrl,
        networkPostbacks: data.networkPostbacks || prev.networkPostbacks
      }));
      showNotification('New secret postback token generated');
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setRegeneratingToken(false);
    }
  };

  // ── Integrations Handlers ──────────────────────────────────────────────────
  const handleSaveIntegrations = async (e) => {
    e.preventDefault();
    setIntegrationsSaving(true);
    try {
      const headers = await getAuthHeader();
      const payload = {
        ga4MeasurementId: integrations.ga4MeasurementId
      };
      if (rawClickBankKey.trim()) payload.clickBankApiKey = rawClickBankKey.trim();
      if (rawMetaToken.trim()) payload.metaToken = rawMetaToken.trim();

      const res = await fetch('/api/settings/integrations', {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to save integrations');
      setRawClickBankKey('');
      setRawMetaToken('');
      showNotification('Integrations updated successfully');
      loadIntegrations();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setIntegrationsSaving(false);
    }
  };

  const handleTestConnection = async (type) => {
    setTestingIntegration(type);
    setTestResult(null);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/settings/integrations/test', {
        method: 'POST',
        headers,
        body: JSON.stringify({ type })
      });
      const data = await res.json();
      setTestResult({
        type,
        success: res.ok && data.success,
        message: data.message || (res.ok ? 'Connection verified!' : 'Connection test failed')
      });
    } catch (err) {
      setTestResult({
        type,
        success: false,
        message: err.message || 'Connection error'
      });
    } finally {
      setTestingIntegration(null);
    }
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/25">
            <CheckCircle2 size={13} className="text-emerald-400" /> Active
          </span>
        );
      case 'verifying_ssl':
      case 'issuing_ssl':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-sky-500/10 text-sky-400 border border-sky-500/25">
            <RefreshCw size={13} className="text-sky-400 animate-spin" /> Verifying SSL
          </span>
        );
      case 'pending_dns':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/25">
            <Clock size={13} className="text-amber-400" /> Pending DNS
          </span>
        );
      case 'error':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-rose-500/10 text-rose-400 border border-rose-500/25">
            <XCircle size={13} className="text-rose-400" /> Error
          </span>
        );
    }
  };

  const formatLastChecked = (ts) => {
    if (!ts) return null;
    try {
      const date = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch (e) {
      return null;
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-950 text-slate-200">
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed top-5 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border text-sm font-bold animate-in slide-in-from-top-4 duration-200 ${
          notification.type === 'error'
            ? 'bg-rose-950/90 text-rose-200 border-rose-500/40 shadow-rose-950/50'
            : 'bg-emerald-950/90 text-emerald-200 border-emerald-500/40 shadow-emerald-950/50'
        }`}>
          {notification.type === 'error' ? <AlertTriangle size={18} className="text-rose-400 shrink-0" /> : <ShieldCheck size={18} className="text-emerald-400 shrink-0" />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Header Bar */}
      <header className="px-8 py-5 border-b border-slate-800 bg-slate-900/60 backdrop-blur shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-xl transition-all border border-slate-700/60 flex items-center justify-center active:scale-95"
            title="Back to Dashboard"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight flex items-center gap-2.5">
              <span>Settings</span>
              <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-[#02B6FF]/15 text-[#33D5E6] border border-[#02B6FF]/30">
                AffiliateFlow v2
              </span>
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Manage custom ad domains, public profile, S2S tracking postbacks, and ad network credentials.
            </p>
          </div>
        </div>
      </header>

      {/* Main Settings Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Navigation Sidebar */}
        <aside className="w-64 border-r border-slate-800 bg-slate-900/40 p-4 space-y-1.5 shrink-0 flex flex-col justify-between">
          <div className="space-y-1.5">
            <button
              onClick={() => setActiveTab('profile')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                activeTab === 'profile'
                  ? 'bg-gradient-to-r from-[#02B6FF]/20 to-indigo-600/20 text-[#33D5E6] border border-[#02B6FF]/30 shadow-lg shadow-[#02B6FF]/5'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
              }`}
            >
              <User size={18} className={activeTab === 'profile' ? 'text-[#33D5E6]' : 'text-slate-400'} />
              <span>Profile</span>
            </button>

            <button
              onClick={() => setActiveTab('domains')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                activeTab === 'domains'
                  ? 'bg-gradient-to-r from-[#02B6FF]/20 to-indigo-600/20 text-[#33D5E6] border border-[#02B6FF]/30 shadow-lg shadow-[#02B6FF]/5'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
              }`}
            >
              <Globe size={18} className={activeTab === 'domains' ? 'text-[#33D5E6]' : 'text-slate-400'} />
              <div className="flex-1 text-left flex items-center justify-between">
                <span>Domains</span>
                {domains.length > 0 && (
                  <span className="text-[10px] bg-slate-800 px-2 py-0.5 rounded-full font-black text-slate-300">
                    {domains.length}
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => setActiveTab('tracking')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                activeTab === 'tracking'
                  ? 'bg-gradient-to-r from-[#02B6FF]/20 to-indigo-600/20 text-[#33D5E6] border border-[#02B6FF]/30 shadow-lg shadow-[#02B6FF]/5'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
              }`}
            >
              <Target size={18} className={activeTab === 'tracking' ? 'text-[#33D5E6]' : 'text-slate-400'} />
              <span>Tracking</span>
            </button>

            <button
              onClick={() => setActiveTab('integrations')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                activeTab === 'integrations'
                  ? 'bg-gradient-to-r from-[#02B6FF]/20 to-indigo-600/20 text-[#33D5E6] border border-[#02B6FF]/30 shadow-lg shadow-[#02B6FF]/5'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
              }`}
            >
              <Cpu size={18} className={activeTab === 'integrations' ? 'text-[#33D5E6]' : 'text-slate-400'} />
              <span>Integrations</span>
            </button>

            <button
              onClick={() => setActiveTab('billing')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                activeTab === 'billing'
                  ? 'bg-gradient-to-r from-[#02B6FF]/20 to-indigo-600/20 text-[#33D5E6] border border-[#02B6FF]/30 shadow-lg shadow-[#02B6FF]/5'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
              }`}
            >
              <CreditCard size={18} className={activeTab === 'billing' ? 'text-[#33D5E6]' : 'text-slate-400'} />
              <span>Billing</span>
            </button>
          </div>

          <div className="p-3 bg-slate-900 border border-slate-800 rounded-2xl text-[11px] text-slate-500 space-y-1">
            <div className="flex items-center gap-1.5 font-bold text-slate-400">
              <ShieldCheck size={14} className="text-emerald-400" /> Direct Ad Engine
            </div>
            <p>Cloudflare for SaaS edge proxy eliminates ad network redirect penalties.</p>
          </div>
        </aside>

        {/* Content Pane */}
        <main className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-300">

            {/* ── 1. PROFILE TAB ────────────────────────────────────────── */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight">Public Profile & Workspace</h2>
                  <p className="text-sm text-slate-400 mt-1">Configure your public identity and default workspace formatting.</p>
                </div>

                <form onSubmit={handleSaveProfile} className="bg-slate-900 border border-slate-800 rounded-3xl p-7 shadow-2xl space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Display Name */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400">Display Name</label>
                      <input
                        type="text"
                        value={profile.displayName}
                        onChange={(e) => setProfile(prev => ({ ...prev, displayName: e.target.value }))}
                        placeholder="Ronald Vance"
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-bold placeholder-slate-600 focus:outline-none focus:border-[#02B6FF]/50 transition-colors"
                      />
                    </div>

                    {/* Email (Read-only) */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400">Account Email (Read-only)</label>
                      <input
                        type="email"
                        value={profile.email}
                        disabled
                        className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800/60 rounded-2xl text-slate-500 font-bold cursor-not-allowed"
                      />
                    </div>

                    {/* Username */}
                    <div className="space-y-2 sm:col-span-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center justify-between">
                        <span>AffiliateFlow Username</span>
                        <span className="text-[10px] text-slate-500 font-normal">Used in /u/:username links</span>
                      </label>
                      <div className="flex items-center">
                        <span className="px-4 py-3 bg-slate-800/80 border border-r-0 border-slate-800 rounded-l-2xl text-slate-500 font-mono text-sm font-bold select-none">
                          affiliateflow.ai/u/
                        </span>
                        <input
                          type="text"
                          value={profile.username}
                          onChange={(e) => setProfile(prev => ({ ...prev, username: e.target.value.toLowerCase().trim() }))}
                          placeholder="rene"
                          pattern="[a-z0-9-]+"
                          required
                          className="flex-1 px-4 py-3 bg-slate-950 border border-slate-800 rounded-r-2xl text-white font-bold font-mono placeholder-slate-600 focus:outline-none focus:border-[#02B6FF]/50 transition-colors"
                        />
                      </div>
                      {initialUsername && profile.username !== initialUsername && (
                        <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-xs text-amber-300 font-bold">
                          <AlertTriangle size={15} className="shrink-0 text-amber-400" />
                          <span>Warning: Changing your username will immediately change all existing <code>/u/{initialUsername}/...</code> URLs!</span>
                        </div>
                      )}
                    </div>

                    {/* Timezone */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400">Reporting Timezone</label>
                      <select
                        value={profile.timezone}
                        onChange={(e) => setProfile(prev => ({ ...prev, timezone: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-bold focus:outline-none focus:border-[#02B6FF]/50 transition-colors"
                      >
                        <option value="America/Los_Angeles">America/Los Angeles (PT)</option>
                        <option value="America/Denver">America/Denver (MT)</option>
                        <option value="America/Chicago">America/Chicago (CT)</option>
                        <option value="America/New_York">America/New York (ET)</option>
                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="Europe/Paris">Europe/Paris (CET)</option>
                        <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                        <option value="UTC">UTC (Universal)</option>
                      </select>
                    </div>

                    {/* Default Currency */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400">Default Currency</label>
                      <select
                        value={profile.defaultCurrency}
                        onChange={(e) => setProfile(prev => ({ ...prev, defaultCurrency: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-bold focus:outline-none focus:border-[#02B6FF]/50 transition-colors"
                      >
                        <option value="USD">USD ($) — US Dollar</option>
                        <option value="EUR">EUR (€) — Euro</option>
                        <option value="GBP">GBP (£) — British Pound</option>
                        <option value="CAD">CAD ($) — Canadian Dollar</option>
                        <option value="AUD">AUD ($) — Australian Dollar</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-800 flex justify-end">
                    <button
                      type="submit"
                      disabled={profileSaving}
                      className="px-6 py-3 bg-gradient-to-r from-[#02B6FF] to-indigo-600 hover:from-[#33D5E6] hover:to-indigo-500 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg shadow-[#02B6FF]/20 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                    >
                      {profileSaving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                      <span>Save Profile</span>
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ── 2. DOMAINS TAB ────────────────────────────────────────── */}
            {activeTab === 'domains' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2.5">
                    <span>Custom Ad Domains</span>
                    <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                      Cloudflare for SaaS
                    </span>
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">
                    Connect your own domains or subdomains (e.g. <code>go.moneyxps.com</code>). Ad networks see a direct, high-trust destination link with zero hop penalties.
                  </p>
                </div>

                {/* Add Domain Card */}
                <form onSubmit={handleConnectDomain} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                  <h3 className="text-sm font-black uppercase tracking-wider text-slate-300">Connect New Domain or Subdomain</h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={newHostname}
                        onChange={(e) => setNewHostname(e.target.value)}
                        placeholder="go.moneyxps.com or mybranddeals.com"
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-bold placeholder-slate-600 focus:outline-none focus:border-[#02B6FF]/50 transition-colors"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={connectingDomain || !newHostname.trim()}
                      className="px-6 py-3 bg-gradient-to-r from-[#02B6FF] to-indigo-600 hover:from-[#33D5E6] hover:to-indigo-500 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg shadow-[#02B6FF]/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 shrink-0"
                    >
                      {connectingDomain ? <RefreshCw size={14} className="animate-spin" /> : <Globe size={14} />}
                      <span>Connect Domain</span>
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    💡 Subdomains like <code>go.yourdomain.com</code> or <code>deals.yourdomain.com</code> work best so your root domain can host your primary website.
                  </p>
                </form>

                {/* Domains List */}
                <div className="space-y-5">
                  <h3 className="text-sm font-black uppercase tracking-wider text-slate-400">Connected Domains ({domains.length})</h3>

                  {domains.length === 0 ? (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 text-center space-y-3">
                      <div className="w-12 h-12 rounded-2xl bg-slate-800 text-slate-500 flex items-center justify-center mx-auto text-xl">
                        🌐
                      </div>
                      <h4 className="text-base font-black text-white">No custom domains connected yet</h4>
                      <p className="text-xs text-slate-400 max-w-md mx-auto">
                        Connect a domain above to host your landing pages directly on your brand's URL. Cloudflare SSL certificates are generated automatically.
                      </p>
                    </div>
                  ) : (
                    domains.map(d => (
                      <div key={d.id} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-6">
                        {/* Domain Top Bar */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">🌐</span>
                            <div>
                              <div className="flex items-center gap-2.5">
                                <span className="text-lg font-black text-white font-mono">{d.hostname}</span>
                                {d.isDefault && (
                                  <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                                    Default Domain
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                Added {d.createdAt ? new Date(d.createdAt._seconds ? d.createdAt._seconds * 1000 : d.createdAt).toLocaleDateString() : 'recently'}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-1.5">
                            <div className="flex items-center gap-3">
                              {renderStatusBadge(d.status)}
                              {d.healthCheck && (
                                <span
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                                    d.healthCheck.ok
                                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                      : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                                  }`}
                                  title={d.healthCheck.ok ? `https://${d.hostname}/health returned ok` : (d.healthCheck.error || 'End-to-end health check failed')}
                                >
                                  {d.healthCheck.ok ? '✓ /health OK' : '⚠ /health Failed'}
                                </span>
                              )}
                              <button
                                onClick={() => handleCheckStatus(d.id)}
                                disabled={checkingDomainId === d.id}
                                className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white rounded-xl text-xs font-bold transition-all border border-slate-700 flex items-center gap-1.5 active:scale-95 disabled:opacity-60"
                                title="Check Cloudflare SSL and end-to-end https://{domain}/health routing"
                              >
                                <RefreshCw size={13} className={checkingDomainId === d.id ? 'animate-spin' : ''} />
                                <span>{checkingDomainId === d.id ? 'Checking...' : 'Check Status'}</span>
                              </button>
                              <button
                                onClick={() => handleDeleteDomain(d.id, d.hostname)}
                                className="p-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-xl transition-all border border-rose-500/20"
                                title="Delete Domain"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                            {d.lastCheckedAt && (
                              <span className="text-[10px] text-slate-500 font-mono">
                                Last checked: {formatLastChecked(d.lastCheckedAt)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Cloudflare Verification Error Banner */}
                        {d.errorMessage && (
                          <div className="p-3.5 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-xs text-rose-300 flex items-start gap-2.5">
                            <AlertTriangle size={15} className="text-rose-400 shrink-0 mt-0.5" />
                            <div>
                              <span className="font-bold text-rose-200">Cloudflare verification error:</span> {d.errorMessage}
                            </div>
                          </div>
                        )}

                        {/* DNS Instructions (Shown if not fully active) */}
                        {d.status !== 'active' && (
                          <div className="bg-slate-950 border border-amber-500/30 rounded-2xl p-5 space-y-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-amber-300 text-xs font-black uppercase tracking-wider">
                                <AlertTriangle size={15} /> Required DNS Records
                              </div>
                              <span className="text-[10px] text-slate-500 font-mono">
                                Cloudflare for SaaS (SSL: HTTP)
                              </span>
                            </div>
                            <p className="text-xs text-slate-400">
                              Add the following DNS records in your domain registrar (Namecheap, GoDaddy, Cloudflare, etc.):
                            </p>

                            <div className="space-y-3 font-mono text-xs">
                              {/* 1. CNAME Record */}
                              <div className="p-3.5 bg-slate-900 border border-slate-800 rounded-xl space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="text-[10px] text-indigo-400 uppercase font-black tracking-widest font-sans">
                                    Record 1: CNAME (Routing Target)
                                  </div>
                                  <span className="text-[10px] text-emerald-400 font-sans font-bold">Required</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-slate-300">
                                  <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80">
                                    <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Type</span>
                                    <span className="font-bold text-white">CNAME</span>
                                  </div>
                                  <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80 flex items-center justify-between">
                                    <div>
                                      <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Host / Name</span>
                                      <span className="font-bold text-white">{getDnsHost(d.hostname)}</span>
                                      <span className="text-[10px] text-slate-500 ml-1.5 font-sans">(or {d.hostname})</span>
                                    </div>
                                    <button
                                      onClick={() => copyToClipboard(getDnsHost(d.hostname), `host-${d.id}`)}
                                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
                                      title="Copy Host"
                                    >
                                      {copiedKey === `host-${d.id}` ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                    </button>
                                  </div>
                                  <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80 flex items-center justify-between">
                                    <div className="overflow-hidden">
                                      <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Target / Value</span>
                                      <span className="font-bold text-sky-400 truncate block">{d.cnameTarget || 'pages.affiliateflow.ai'}</span>
                                    </div>
                                    <button
                                      onClick={() => copyToClipboard(d.cnameTarget || 'pages.affiliateflow.ai', `cname-${d.id}`)}
                                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors shrink-0 ml-1.5"
                                      title="Copy Target"
                                    >
                                      {copiedKey === `cname-${d.id}` ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* 2. Cloudflare Ownership TXT Record */}
                              {d.ownershipVerification && d.ownershipVerification.value ? (
                                <div className="p-3.5 bg-slate-900 border border-slate-800 rounded-xl space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="text-[10px] text-amber-400 uppercase font-black tracking-widest font-sans">
                                      Record 2: TXT (Cloudflare Ownership Verification)
                                    </div>
                                    <span className="text-[10px] text-amber-400 font-sans font-bold">Required</span>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-slate-300">
                                    <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80">
                                      <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Type</span>
                                      <span className="font-bold text-white">TXT</span>
                                    </div>
                                    <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80 flex items-center justify-between sm:col-span-2">
                                      <div className="overflow-hidden">
                                        <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Host / Name</span>
                                        <span className="font-bold text-white break-all">{d.ownershipVerification.name}</span>
                                      </div>
                                      <button
                                        onClick={() => copyToClipboard(d.ownershipVerification.name, `txtname-${d.id}`)}
                                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors shrink-0 ml-2"
                                        title="Copy TXT Name"
                                      >
                                        {copiedKey === `txtname-${d.id}` ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                      </button>
                                    </div>
                                    <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80 flex items-center justify-between sm:col-span-3">
                                      <div className="overflow-hidden">
                                        <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Value / Text</span>
                                        <span className="font-bold text-amber-300 break-all">{d.ownershipVerification.value}</span>
                                      </div>
                                      <button
                                        onClick={() => copyToClipboard(d.ownershipVerification.value, `txtval-${d.id}`)}
                                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors shrink-0 ml-2"
                                        title="Copy TXT Value"
                                      >
                                        {copiedKey === `txtval-${d.id}` ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              {/* 3. Cloudflare SSL Certificate Validation Record(s) */}
                              {d.sslValidationRecords && d.sslValidationRecords.length > 0 ? (
                                d.sslValidationRecords.map((sslRec, idx) => (
                                  <div key={idx} className="p-3.5 bg-slate-900 border border-slate-800 rounded-xl space-y-2">
                                    <div className="flex items-center justify-between">
                                      <div className="text-[10px] text-sky-400 uppercase font-black tracking-widest font-sans">
                                        Record {d.ownershipVerification ? 3 + idx : 2 + idx}: TXT (SSL Certificate Validation)
                                      </div>
                                      <span className="text-[10px] text-sky-400 font-sans font-bold">CA Validation</span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-slate-300">
                                      <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80">
                                        <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Type</span>
                                        <span className="font-bold text-white">TXT</span>
                                      </div>
                                      <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80 flex items-center justify-between sm:col-span-2">
                                        <div className="overflow-hidden">
                                          <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Host / Name</span>
                                          <span className="font-bold text-white break-all">{sslRec.txt_name}</span>
                                        </div>
                                        <button
                                          onClick={() => copyToClipboard(sslRec.txt_name, `sslname-${d.id}-${idx}`)}
                                          className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors shrink-0 ml-2"
                                        >
                                          {copiedKey === `sslname-${d.id}-${idx}` ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                        </button>
                                      </div>
                                      <div className="bg-slate-950/80 p-2.5 rounded-lg border border-slate-800/80 flex items-center justify-between sm:col-span-3">
                                        <div className="overflow-hidden">
                                          <span className="text-slate-500 text-[10px] uppercase block font-sans font-bold">Value / Text</span>
                                          <span className="font-bold text-sky-300 break-all">{sslRec.txt_value}</span>
                                        </div>
                                        <button
                                          onClick={() => copyToClipboard(sslRec.txt_value, `sslval-${d.id}-${idx}`)}
                                          className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors shrink-0 ml-2"
                                        >
                                          {copiedKey === `sslval-${d.id}-${idx}` ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl text-[11px] text-slate-400 font-sans flex items-center gap-2">
                                  <Clock size={13} className="text-sky-400 shrink-0" />
                                  <span>HTTP SSL validation active: Cloudflare automatically provisions and activates SSL once your CNAME resolves to {d.cnameTarget || 'pages.affiliateflow.ai'}.</span>
                                </div>
                              )}
                            </div>

                            <p className="text-[11px] text-slate-500 italic">
                              ⏱️ DNS propagation takes 2–10 minutes. The status badge will automatically update from Pending to Active once verified.
                            </p>
                          </div>
                        )}

                        {/* Domain Controls (Default & Root Behavior) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                          {/* Default Toggle */}
                          <div className="p-4 bg-slate-950 border border-slate-800/80 rounded-2xl flex items-center justify-between">
                            <div>
                              <div className="text-xs font-black uppercase tracking-wider text-white">Default Domain</div>
                              <p className="text-[11px] text-slate-400 mt-0.5">Preselect for all newly generated funnels</p>
                            </div>
                            <button
                              onClick={() => !d.isDefault && handleSetDefaultDomain(d.id)}
                              disabled={d.isDefault}
                              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                d.isDefault
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-default'
                                  : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700'
                              }`}
                            >
                              {d.isDefault ? '✓ Default' : 'Set as Default'}
                            </button>
                          </div>

                          {/* Root Domain Behavior */}
                          <div className="p-4 bg-slate-950 border border-slate-800/80 rounded-2xl space-y-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-black uppercase tracking-wider text-white">Root URL Behavior (/)</span>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={d.rootBehavior || '404'}
                                onChange={(e) => handleSaveRootBehavior(d.id, e.target.value, d.rootRedirectSlug)}
                                className="flex-1 px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 focus:outline-none"
                              >
                                <option value="404">Return 404 (Standard)</option>
                                <option value="redirect">Serve Campaign Landing Page</option>
                              </select>

                              {d.rootBehavior === 'redirect' && (
                                <select
                                  value={d.rootRedirectSlug || ''}
                                  onChange={(e) => handleSaveRootBehavior(d.id, 'redirect', e.target.value)}
                                  className="flex-1 px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-xl text-xs font-bold text-[#33D5E6] focus:outline-none"
                                >
                                  <option value="">-- Choose Campaign --</option>
                                  {userCampaignSlugs.map(s => (
                                    <option key={s.id} value={s.slug}>/{s.slug} ({s.name})</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>

                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* ── 3. TRACKING TAB ───────────────────────────────────────── */}
            {activeTab === 'tracking' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight">S2S Conversion Postbacks & Tracking</h2>
                  <p className="text-sm text-slate-400 mt-1">Configure server-to-server affiliate network postback webhooks and default campaign traffic models.</p>
                </div>

                {/* Per-Network S2S Postbacks Card */}
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-7 shadow-2xl space-y-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-black uppercase tracking-wider text-white flex items-center gap-2">
                        <Target size={16} className="text-[#33D5E6]" /> Ready-to-Paste Postback URLs by Network (S2S)
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Each network card below has that network&apos;s verified S2S macros and your secret token pre-filled. When <code className="text-indigo-300">/r/</code> redirects to an offer, AffiliateFlow automatically appends the click ID using that network&apos;s sub-ID parameter.
                      </p>
                    </div>
                    <div className="flex items-center gap-2.5">
                      {tracking.postbackToken && (
                        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-xl bg-slate-950 border border-slate-800 font-mono text-[11px] text-slate-400">
                          <span>
                            Token:{' '}
                            <strong className={showPostbackToken ? 'text-emerald-400' : 'text-slate-500 select-none'}>
                              {showPostbackToken ? tracking.postbackToken : '••••••••••••'}
                            </strong>
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPostbackToken(prev => !prev)}
                            className="px-1.5 py-0.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-sans font-bold text-[10px] border border-slate-700/70 inline-flex items-center gap-1 transition-colors"
                            aria-label={showPostbackToken ? 'Hide postback token' : 'Show postback token'}
                          >
                            {showPostbackToken ? <EyeOff size={11} /> : <Eye size={11} />}
                            <span>{showPostbackToken ? 'Hide' : 'Show'}</span>
                          </button>
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={handleRegenerateToken}
                        disabled={regeneratingToken}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-rose-400 rounded-xl text-xs font-bold transition-all border border-slate-700/60 flex items-center gap-1.5"
                      >
                        <RefreshCw size={12} className={regeneratingToken ? 'animate-spin' : ''} />
                        <span>Regenerate Token</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {Object.entries(NETWORK_TRACKING_CONFIGS).map(([netKey, baseCfg]) => {
                      const serverCfg = tracking.networkPostbacks?.[netKey] || {};
                      const netCfg = { ...baseCfg, ...serverCfg };
                      const chosenSubId = tracking.networkSubIds?.[netKey] || netCfg.subIdParam || baseCfg.subIdParam;
                      const subIdOptions = baseCfg.subIdOptions || [baseCfg.subIdParam];
                      const fullUrl = buildNetworkPostbackUrlFromConfig(netKey, tracking.postbackToken, 'affiliateflow.ai', chosenSubId) || netCfg.postbackUrl || netCfg.url || '';
                      const displayFullUrl = showPostbackToken
                        ? fullUrl
                        : fullUrl.replace(/([?&]token=)[^&]+/, '$1••••••••••••');
                      const insUrl = netKey === 'ClickBank' && tracking.postbackToken
                        ? `https://affiliateflow.ai/api/postback?network=ClickBank&token=${tracking.postbackToken}`
                        : '';
                      const displayInsUrl = showPostbackToken
                        ? insUrl
                        : insUrl.replace(/([?&]token=)[^&]+/, '$1••••••••••••');

                      return (
                        <div key={netKey} className="p-5 bg-slate-950 border border-slate-800/90 rounded-2xl space-y-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2.5">
                              <span className="text-xs font-black text-white uppercase tracking-wider">{netCfg.name || netCfg.label || netKey}</span>
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[10px] font-mono font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                                <span>AffiliateFlow Sub-ID:</span>
                                <select
                                  value={chosenSubId}
                                  onChange={(e) => handleSubIdChange(netKey, e.target.value)}
                                  aria-label={`${netKey} Sub-ID parameter`}
                                  className="bg-slate-900 text-white font-mono text-[10px] font-black px-1.5 py-0.5 rounded border border-indigo-500/40 focus:outline-none"
                                >
                                  {subIdOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <span className="text-slate-400">(&rarr; ?{chosenSubId}=&#123;clickId&#125;)</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              disabled={!fullUrl}
                              onClick={() => copyToClipboard(fullUrl, `pb_${netKey}`)}
                              className="px-3.5 py-1.5 bg-[#02B6FF]/15 hover:bg-[#02B6FF]/25 disabled:opacity-40 text-[#33D5E6] border border-[#02B6FF]/30 rounded-xl text-xs font-bold flex items-center gap-1.5 active:scale-95 transition-all"
                            >
                              {copiedKey === `pb_${netKey}` ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                              <span>{copiedKey === `pb_${netKey}` ? 'Copied!' : `Copy ${netKey} URL`}</span>
                            </button>
                          </div>

                          <input
                            type="text"
                            readOnly
                            value={displayFullUrl || 'Loading postback URL...'}
                            onFocus={(e) => e.target.select()}
                            aria-label={`${netKey} Postback URL`}
                            className="w-full font-mono text-[11px] text-indigo-300 bg-slate-900/90 px-3.5 py-2.5 rounded-xl border border-slate-800 focus:outline-none focus:border-[#02B6FF]/50 select-all"
                          />

                          {netKey === 'ClickBank' && insUrl && (
                            <div className="pt-2 border-t border-slate-800/70 space-y-2">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="text-[11px] font-bold text-slate-300">
                                  ClickBank Instant Notification Service (INS v8.0 Encrypted POST Webhook URL):
                                </span>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(insUrl, 'pb_ClickBank_INS')}
                                  className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-[11px] font-bold flex items-center gap-1.5 active:scale-95 transition-all"
                                >
                                  {copiedKey === 'pb_ClickBank_INS' ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                  <span>{copiedKey === 'pb_ClickBank_INS' ? 'Copied!' : 'Copy INS Webhook URL'}</span>
                                </button>
                              </div>
                              <input
                                type="text"
                                readOnly
                                value={displayInsUrl}
                                onFocus={(e) => e.target.select()}
                                aria-label="ClickBank INS Webhook URL"
                                className="w-full font-mono text-[11px] text-amber-300 bg-slate-900/70 px-3.5 py-2 rounded-xl border border-slate-800/80 focus:outline-none select-all"
                              />
                            </div>
                          )}

                          {(netCfg.notes || netCfg.docsSource) && (
                            <p className="text-[11px] text-slate-500 leading-relaxed">{netCfg.notes || netCfg.docsSource}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── Postback Forwarding (Multi-Tenant SaaS Feature) ───────────── */}
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-7 shadow-2xl space-y-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-black uppercase tracking-wider text-white flex items-center gap-2">
                        <ExternalLink size={16} className="text-[#33D5E6]" /> Postback Forwarding
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        For networks like MaxBounty that allow only ONE global postback URL: let AffiliateFlow receive the global postback and automatically forward conversions that don&apos;t belong to AffiliateFlow (or a copy of all conversions) to your existing tracker. Up to 3 rules per network.
                      </p>
                    </div>
                  </div>

                  {/* Add / Edit Forwarding Rule Form */}
                  <form onSubmit={handleSaveForwardRule} className="p-5 bg-slate-950 border border-slate-800/90 rounded-2xl space-y-4">
                    <div className="text-xs font-black uppercase tracking-wider text-slate-300">Add Forwarding Rule</div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Network</label>
                        <select
                          value={newForwardRule.network}
                          onChange={(e) => setNewForwardRule(prev => ({ ...prev, network: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white font-bold focus:outline-none focus:border-[#02B6FF]/50"
                        >
                          <option value="MaxBounty">MaxBounty</option>
                          <option value="CPALead">CPALead</option>
                          <option value="ClickBank">ClickBank</option>
                          <option value="Custom">Custom Network</option>
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Rule Label</label>
                        <input
                          type="text"
                          value={newForwardRule.name}
                          onChange={(e) => setNewForwardRule(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g. Existing Organic Tracker"
                          className="w-full px-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 font-bold focus:outline-none focus:border-[#02B6FF]/50"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Forwarding Condition</label>
                        <select
                          value={newForwardRule.condition}
                          onChange={(e) => setNewForwardRule(prev => ({ ...prev, condition: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white font-bold focus:outline-none focus:border-[#02B6FF]/50"
                        >
                          <option value="unmatched">Forward when conversion doesn&apos;t match an AffiliateFlow click (Default)</option>
                          <option value="always">Always forward (copy)</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Destination URL Template (HTTPS required)
                      </label>
                      <input
                        type="text"
                        value={newForwardRule.destinationUrlTemplate}
                        onChange={(e) => setNewForwardRule(prev => ({ ...prev, destinationUrlTemplate: e.target.value }))}
                        placeholder="https://your-tracker.com/postback?click_id={s1}&payout={payout}&tx_id={tx_id}"
                        className="w-full px-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl font-mono text-xs text-indigo-300 placeholder-slate-500 focus:outline-none focus:border-[#02B6FF]/50"
                      />
                      <p className="text-[11px] text-slate-500">
                        Supported placeholders: <code className="text-indigo-300">&#123;s1&#125;</code>–<code className="text-indigo-300">&#123;s5&#125;</code>, <code className="text-indigo-300">&#123;payout&#125;</code>, <code className="text-indigo-300">&#123;tx_id&#125;</code>, <code className="text-indigo-300">&#123;offer_id&#125;</code>, <code className="text-indigo-300">&#123;status&#125;</code>, and <code className="text-indigo-300">&#123;param:&lt;name&gt;&#125;</code> for any raw parameter.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={testingRuleId === 'draft' || !newForwardRule.destinationUrlTemplate.trim()}
                          onClick={() => handleTestForwardRule({ ...newForwardRule, isDraft: true }, 'draft')}
                          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-200 rounded-xl text-xs font-bold border border-slate-700 flex items-center gap-1.5 transition-all"
                        >
                          <RefreshCw size={12} className={testingRuleId === 'draft' ? 'animate-spin' : ''} />
                          <span>{testingRuleId === 'draft' ? 'Testing...' : 'Send Test'}</span>
                        </button>
                        {forwardTestStatus.draft && (
                          <span className={`px-2.5 py-1 rounded-lg text-[11px] font-mono font-bold border ${
                            forwardTestStatus.draft.ok
                              ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                              : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                          }`}>
                            {forwardTestStatus.draft.statusCode
                              ? `HTTP ${forwardTestStatus.draft.statusCode} (${forwardTestStatus.draft.durationMs}ms)`
                              : forwardTestStatus.draft.error || 'Blocked / Failed'}
                          </span>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={savingForwardRule || !newForwardRule.destinationUrlTemplate.trim()}
                        className="px-5 py-2 bg-[#02B6FF] hover:bg-[#33D5E6] disabled:opacity-50 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5"
                      >
                        <Check size={14} />
                        <span>{savingForwardRule ? 'Saving...' : 'Add Forwarding Rule'}</span>
                      </button>
                    </div>
                  </form>

                  {/* Configured Forwarding Rules List */}
                  <div className="space-y-3">
                    <div className="text-xs font-black uppercase tracking-wider text-slate-300">
                      Configured Forwarding Rules ({(tracking.forwardingRules || []).length})
                    </div>
                    {(!tracking.forwardingRules || tracking.forwardingRules.length === 0) ? (
                      <div className="p-4 bg-slate-950/60 border border-slate-800/80 rounded-2xl text-xs text-slate-500">
                        No forwarding rules configured yet.
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {tracking.forwardingRules.map((rule) => {
                          const testStat = forwardTestStatus[rule.id];
                          return (
                            <div key={rule.id} className="p-4 bg-slate-950 border border-slate-800/90 rounded-2xl space-y-2">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                    {rule.network}
                                  </span>
                                  <span className="text-xs font-bold text-white">{rule.name || `${rule.network} Rule`}</span>
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-slate-900 text-slate-400 border border-slate-800">
                                    {rule.condition === 'always'
                                      ? 'Always forward (copy)'
                                      : 'Forward when unmatched in AffiliateFlow'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {testStat && (
                                    <span className={`px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold border ${
                                      testStat.ok
                                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                        : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                                    }`}>
                                      {testStat.statusCode ? `HTTP ${testStat.statusCode} (${testStat.durationMs}ms)` : (testStat.error || 'Error')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    disabled={testingRuleId === rule.id}
                                    onClick={() => handleTestForwardRule(rule, rule.id)}
                                    className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-[11px] font-bold border border-slate-700 flex items-center gap-1"
                                  >
                                    <RefreshCw size={11} className={testingRuleId === rule.id ? 'animate-spin' : ''} />
                                    <span>{testingRuleId === rule.id ? 'Testing...' : 'Send Test'}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteForwardRule(rule.id)}
                                    className="p-1.5 bg-slate-900 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 rounded-xl border border-slate-800 transition-colors"
                                    title="Delete rule"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>
                              <input
                                type="text"
                                readOnly
                                value={rule.destinationUrlTemplate}
                                onFocus={(e) => e.target.select()}
                                className="w-full font-mono text-[11px] text-emerald-300 bg-slate-900/80 px-3 py-2 rounded-xl border border-slate-800/80 focus:outline-none select-all"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Forward Log Table (Last 100) */}
                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-black uppercase tracking-wider text-slate-300">
                          Forward Log (Last 100)
                        </h4>
                        <p className="text-[11px] text-slate-500">
                          Idempotent by <code className="text-slate-400">tx_id</code> per rule · Retries failed forwards up to 3 times with backoff.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRefreshForwardLogs}
                        disabled={loadingForwardLogs}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold border border-slate-700 flex items-center gap-1.5"
                      >
                        <RefreshCw size={12} className={loadingForwardLogs ? 'animate-spin' : ''} />
                        <span>Refresh Log</span>
                      </button>
                    </div>

                    {(!tracking.forwardLogs || tracking.forwardLogs.length === 0) ? (
                      <div className="p-4 bg-slate-950/60 border border-slate-800/80 rounded-2xl text-xs text-slate-500">
                        No forwarded postbacks recorded yet.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-slate-800 rounded-2xl bg-slate-950">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead>
                            <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-wider text-slate-400 bg-slate-900/60">
                              <th className="py-2.5 px-3">Time</th>
                              <th className="py-2.5 px-3">Network</th>
                              <th className="py-2.5 px-3">Rule</th>
                              <th className="py-2.5 px-3">Destination Host</th>
                              <th className="py-2.5 px-3">Status Code</th>
                              <th className="py-2.5 px-3">Retries</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/60 font-mono text-[11px]">
                            {tracking.forwardLogs.slice(0, 100).map((log, idx) => (
                              <tr key={log.id || idx} className="hover:bg-slate-900/40">
                                <td className="py-2 px-3 text-slate-400 whitespace-nowrap">
                                  {log.timestampMs ? new Date(log.timestampMs).toLocaleString() : 'Just now'}
                                </td>
                                <td className="py-2 px-3 text-white font-sans font-bold">
                                  {log.network}
                                  {log.isTest && (
                                    <span className="ml-1.5 px-1.5 py-0.5 text-[9px] rounded bg-amber-500/20 text-amber-300 font-mono">TEST</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-slate-300 font-sans">{log.ruleName || log.ruleId}</td>
                                <td className="py-2 px-3 text-indigo-300">{log.destinationHost || '-'}</td>
                                <td className="py-2 px-3">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                                    log.ok
                                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                      : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                                  }`}>
                                    {log.statusCode ? `HTTP ${log.statusCode}` : (log.error || 'ERR')}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-slate-400">{log.retries ?? 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tracking Defaults Form */}
                <form onSubmit={handleSaveTracking} className="bg-slate-900 border border-slate-800 rounded-3xl p-7 shadow-2xl space-y-6">
                  <h3 className="text-sm font-black uppercase tracking-wider text-slate-300">Campaign Traffic Defaults</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400">Default Traffic Source</label>
                      <select
                        value={tracking.defaultTrafficSource}
                        onChange={(e) => setTracking(prev => ({ ...prev, defaultTrafficSource: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-bold focus:outline-none focus:border-[#02B6FF]/50"
                      >
                        <option value="MGID">MGID (Native Ads)</option>
                        <option value="Taboola">Taboola (Native Ads)</option>
                        <option value="Outbrain">Outbrain (Native Ads)</option>
                        <option value="Google Ads">Google Ads</option>
                        <option value="Facebook Ads">Facebook Ads</option>
                        <option value="TikTok Ads">TikTok Ads</option>
                        <option value="YouTube Ads">YouTube Ads</option>
                        <option value="Email / Solo Ads">Email / Solo Ads</option>
                        <option value="Organic / SEO">Organic / SEO</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400">Default Cost Model</label>
                      <select
                        value={tracking.defaultCostModel}
                        onChange={(e) => setTracking(prev => ({ ...prev, defaultCostModel: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-bold focus:outline-none focus:border-[#02B6FF]/50"
                      >
                        <option value="CPC">CPC — Cost Per Click</option>
                        <option value="CPM">CPM — Cost Per Mille (Impressions)</option>
                        <option value="CPA">CPA — Cost Per Acquisition</option>
                      </select>
                    </div>
                  </div>

                  {/* Offer Health Check Toggle */}
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-between">
                    <div>
                      <div className="text-xs font-black uppercase tracking-wider text-white">Offer Link Health Check</div>
                      <p className="text-xs text-slate-500 mt-0.5">Automated daily check to verify external merchant offer links still resolve</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tracking.offerHealthCheck}
                        onChange={(e) => setTracking(prev => ({ ...prev, offerHealthCheck: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>

                  <div className="pt-4 border-t border-slate-800 flex justify-end">
                    <button
                      type="submit"
                      disabled={trackingSaving}
                      className="px-6 py-3 bg-gradient-to-r from-[#02B6FF] to-indigo-600 hover:from-[#33D5E6] hover:to-indigo-500 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg shadow-[#02B6FF]/20 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                    >
                      {trackingSaving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                      <span>Save Tracking Defaults</span>
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ── 4. INTEGRATIONS TAB ───────────────────────────────────── */}
            {activeTab === 'integrations' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight">Ad Networks & Analytics Integrations</h2>
                  <p className="text-sm text-slate-400 mt-1">Connect your network APIs for direct tracking and automated angle research. Credentials are encrypted and stored server-only.</p>
                </div>

                {testResult && (
                  <div className={`p-4 rounded-2xl border text-xs font-bold flex items-center gap-2.5 animate-in fade-in duration-200 ${
                    testResult.success 
                      ? 'bg-emerald-950/80 border-emerald-500/30 text-emerald-300' 
                      : 'bg-rose-950/80 border-rose-500/30 text-rose-300'
                  }`}>
                    {testResult.success ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                    <span>{testResult.message}</span>
                  </div>
                )}

                <form onSubmit={handleSaveIntegrations} className="space-y-6">
                  {/* Google Analytics 4 Card */}
                  <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center font-bold">
                          GA4
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-white">Google Analytics 4</h3>
                          <p className="text-xs text-slate-400">Stream client-side engagement events directly to your GA4 property.</p>
                        </div>
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
                        {integrations.ga4MeasurementId ? 'Configured' : 'Optional'}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400">Measurement ID</label>
                      <input
                        type="text"
                        value={integrations.ga4MeasurementId}
                        onChange={(e) => setIntegrations(prev => ({ ...prev, ga4MeasurementId: e.target.value.trim() }))}
                        placeholder="G-XXXXXXXXXX"
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-[#02B6FF]/50"
                      />
                    </div>
                  </div>

                  {/* ClickBank API Key Card */}
                  <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center font-bold">
                          CB
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-white">ClickBank Developer API</h3>
                          <p className="text-xs text-slate-400">Enables automated marketplace stats sync, commission pulls, and offer imports.</p>
                        </div>
                      </div>
                      {integrations.hasClickBankKey ? (
                        <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                          ✓ Saved Server-Side
                        </span>
                      ) : (
                        <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-800 text-slate-500 border border-slate-700">
                          Not Connected
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center justify-between">
                        <span>API Developer Key</span>
                        {integrations.hasClickBankKey && (
                          <span className="text-slate-500 font-mono text-[11px]">{integrations.clickBankKeyMasked}</span>
                        )}
                      </label>
                      <input
                        type="password"
                        value={rawClickBankKey}
                        onChange={(e) => setRawClickBankKey(e.target.value)}
                        placeholder={integrations.hasClickBankKey ? "Enter new key to replace existing" : "API-XXXXXXXXXXXXXXXXXXXXXXXXXXXX"}
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-[#02B6FF]/50"
                      />
                    </div>

                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => handleTestConnection('clickbank')}
                        disabled={testingIntegration === 'clickbank' || (!integrations.hasClickBankKey && !rawClickBankKey)}
                        className="px-4 py-2 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white rounded-xl text-xs font-bold transition-all border border-slate-700 flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {testingIntegration === 'clickbank' ? <RefreshCw size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                        <span>Test Connection</span>
                      </button>
                    </div>
                  </div>

                  {/* Meta Ad Library Access Token Card */}
                  <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center font-bold">
                          FB
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-white">Meta Ad Library Access Token</h3>
                          <p className="text-xs text-slate-400">Used by Ad Studio and Spy Competition to pull winning competitor ad creatives.</p>
                        </div>
                      </div>
                      {integrations.hasMetaToken ? (
                        <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                          ✓ Saved Server-Side
                        </span>
                      ) : (
                        <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-800 text-slate-500 border border-slate-700">
                          Not Connected
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center justify-between">
                        <span>Graph API Access Token</span>
                        {integrations.hasMetaToken && (
                          <span className="text-slate-500 font-mono text-[11px]">{integrations.metaTokenMasked}</span>
                        )}
                      </label>
                      <input
                        type="password"
                        value={rawMetaToken}
                        onChange={(e) => setRawMetaToken(e.target.value)}
                        placeholder={integrations.hasMetaToken ? "Enter new token to replace existing" : "EAA..."}
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-white font-mono text-sm placeholder-slate-600 focus:outline-none focus:border-[#02B6FF]/50"
                      />
                    </div>

                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => handleTestConnection('meta')}
                        disabled={testingIntegration === 'meta' || (!integrations.hasMetaToken && !rawMetaToken)}
                        className="px-4 py-2 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white rounded-xl text-xs font-bold transition-all border border-slate-700 flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {testingIntegration === 'meta' ? <RefreshCw size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                        <span>Test Connection</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={integrationsSaving}
                      className="px-6 py-3 bg-gradient-to-r from-[#02B6FF] to-indigo-600 hover:from-[#33D5E6] hover:to-indigo-500 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg shadow-[#02B6FF]/20 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                    >
                      {integrationsSaving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                      <span>Save Integrations</span>
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ── 5. BILLING TAB (STUB) ─────────────────────────────────── */}
            {activeTab === 'billing' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight">Billing & Subscription</h2>
                  <p className="text-sm text-slate-400 mt-1">Manage your plan, features, and active entitlements.</p>
                </div>

                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl space-y-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#02B6FF]/10 to-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-gradient-to-r from-[#02B6FF]/20 to-indigo-600/20 text-[#33D5E6] border border-[#02B6FF]/30">
                        Active Membership
                      </span>
                      <h3 className="text-3xl font-black text-white tracking-tight mt-3">Pro Stealth Engine</h3>
                      <p className="text-xs text-slate-400 mt-1">Unlimited Custom Domains & Direct Landing Pages</p>
                    </div>

                    <div className="px-5 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 font-black text-xs uppercase tracking-wider flex items-center gap-2">
                      <Sparkles size={16} /> Entitled
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-800">
                    <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl space-y-1">
                      <div className="text-xs font-bold text-white flex items-center gap-1.5">
                        <Check size={14} className="text-emerald-400" /> Custom Domains for SaaS
                      </div>
                      <p className="text-[11px] text-slate-500">Unlimited custom hostnames with automated Cloudflare SSL certificates.</p>
                    </div>

                    <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl space-y-1">
                      <div className="text-xs font-bold text-white flex items-center gap-1.5">
                        <Check size={14} className="text-emerald-400" /> Zero-Hop Ad Direct Hosting
                      </div>
                      <p className="text-[11px] text-slate-500">Serve landing pages directly on your domain with no redirect on ad hop.</p>
                    </div>

                    <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl space-y-1">
                      <div className="text-xs font-bold text-white flex items-center gap-1.5">
                        <Check size={14} className="text-emerald-400" /> S2S Postbacks & Conversion Tracking
                      </div>
                      <p className="text-[11px] text-slate-500">Real-time webhook postbacks for ClickBank, MaxBounty, and CPALead.</p>
                    </div>

                    <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl space-y-1">
                      <div className="text-xs font-bold text-white flex items-center gap-1.5">
                        <Check size={14} className="text-emerald-400" /> Neural Pre-lander Generator
                      </div>
                      <p className="text-[11px] text-slate-500">High-converting multi-angle bridge pages generated via Claude & Gemini.</p>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-2xl text-center space-y-1">
                    <div className="text-xs font-bold text-slate-400">Self-serve payment portal & invoicing</div>
                    <p className="text-[11px] text-slate-500">Coming soon in the next release.</p>
                  </div>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}
